import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PencilLine, Eraser, Trash2, X, Palette } from "lucide-react";

const COLORS = ["#FFFFFF", "#3B82F6", "#A855F7", "#34D399", "#F59E0B", "#F43F5E"];

/**
 * Lightweight in-game whiteboard. Canvas-based, supports mouse + touch.
 * Sits as an overlay above the question so the player can do rough work for
 * math/science problems without leaving the match.
 */
export default function Whiteboard({ open, onClose, resetKey }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  // Persisted drawing (data URL). Lives on the mounted Whiteboard instance so
  // work survives closing/reopening the board within the same question.
  const savedImage = useRef(null);
  const [tool, setTool] = useState("pen");          // pen | eraser
  const [color, setColor] = useState("#FFFFFF");
  const [size, setSize] = useState(3);

  const saveSnapshot = () => {
    const c = canvasRef.current;
    if (!c || !c.width || !c.height) return;
    try { savedImage.current = c.toDataURL(); } catch (_) { /* ignore */ }
  };

  // Initialize canvas size to its display size (HiDPI aware)
  useEffect(() => {
    if (!open) return undefined;
    const sync = () => {
      const c = canvasRef.current;
      const wrap = wrapRef.current;
      if (!c || !wrap) return;
      const ratio = window.devicePixelRatio || 1;
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      // No snapshot here: savedImage is already kept current by every stroke's
      // mouseup (saveSnapshot) and by clearAll. Capturing the canvas here would
      // race the async restore below — on remount (and under StrictMode's
      // double-invoked effects) it reads a blank canvas and wipes the saved
      // drawing.
      c.width = w * ratio;
      c.height = h * ratio;
      c.style.width = `${w}px`;
      c.style.height = `${h}px`;
      const ctx = c.getContext("2d");
      ctx.scale(ratio, ratio);
      ctx.lineCap = "round"; ctx.lineJoin = "round";
      // Restore persisted work (survives close/reopen within the same question).
      if (savedImage.current) {
        const img = new Image();
        img.onload = () => ctx.drawImage(img, 0, 0, w, h);
        img.src = savedImage.current;
      }
    };
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, [open]);

  const getPos = (evt) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const touch = evt.touches?.[0] || evt.changedTouches?.[0];
    const cx = (touch ? touch.clientX : evt.clientX) - rect.left;
    const cy = (touch ? touch.clientY : evt.clientY) - rect.top;
    return { x: cx, y: cy };
  };

  const begin = (evt) => {
    evt.preventDefault();
    drawing.current = true;
    last.current = getPos(evt);
  };
  const move = (evt) => {
    if (!drawing.current) return;
    evt.preventDefault();
    const p = getPos(evt);
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = tool === "eraser" ? "rgba(0,0,0,1)" : color;
    ctx.globalCompositeOperation = tool === "eraser" ? "destination-out" : "source-over";
    ctx.lineWidth = tool === "eraser" ? size * 4 : size;
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => { drawing.current = false; saveSnapshot(); };

  const clearAll = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.restore();
    savedImage.current = null;
  };

  // Wipe the board when the question changes or the match ends. Skips the very
  // first render so simply opening the board never erases existing work.
  const firstReset = useRef(true);
  useEffect(() => {
    if (firstReset.current) { firstReset.current = false; return; }
    savedImage.current = null;
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.restore();
  }, [resetKey]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] bg-[#02040D]/80 backdrop-blur-md p-3 sm:p-6 flex flex-col"
          data-testid="whiteboard-modal"
        >
          {/* Toolbar */}
          <div className="sm-card flex flex-wrap items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 mb-3"
            style={{ borderColor: "rgba(59,130,246,0.5)" }}>
            <div className="font-mono text-[10px] tracking-[0.3em] text-blue uppercase pr-2 border-r border-white/10">
              Whiteboard
            </div>
            <ToolBtn active={tool === "pen"}    onClick={() => setTool("pen")}    label="Pen"    icon={<PencilLine size={14} />} testid="wb-tool-pen" />
            <ToolBtn active={tool === "eraser"} onClick={() => setTool("eraser")} label="Eraser" icon={<Eraser size={14} />}      testid="wb-tool-eraser" />

            {/* Colors */}
            <div className="flex items-center gap-1.5 pl-1 sm:pl-3 sm:border-l border-white/10">
              <Palette size={12} className="text-faint" />
              {COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => { setColor(c); setTool("pen"); }}
                  className={`w-5 h-5 rounded-full border ${color === c && tool === "pen" ? "ring-2 ring-white/80" : "border-white/20"}`}
                  style={{ background: c }}
                  data-testid={`wb-color-${c}`}
                  aria-label={`Color ${c}`}
                />
              ))}
            </div>

            {/* Size */}
            <div className="flex items-center gap-1 pl-1 sm:pl-3 sm:border-l border-white/10 text-faint">
              <span className="font-mono text-[10px] tracking-widest hidden sm:inline">SIZE</span>
              <input
                type="range" min={1} max={12} value={size}
                onChange={(e) => setSize(parseInt(e.target.value, 10))}
                className="accent-blue-500 w-20 sm:w-24"
                data-testid="wb-size"
              />
            </div>

            <div className="flex-1" />
            <button onClick={clearAll} className="sm-btn-outline px-3 py-2 text-xs flex items-center gap-1.5" data-testid="wb-clear">
              <Trash2 size={12} /> CLEAR
            </button>
            <button
              onClick={onClose}
              className="px-3 py-2 rounded-lg border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-xs font-mono uppercase tracking-widest flex items-center gap-1.5"
              data-testid="wb-close"
            >
              <X size={12} /> CLOSE
            </button>
          </div>

          {/* Canvas */}
          <div
            ref={wrapRef}
            className="flex-1 sm-card overflow-hidden relative"
            style={{ background: "#0B0E1E" }}
          >
            <canvas
              ref={canvasRef}
              className="w-full h-full touch-none cursor-crosshair"
              onMouseDown={begin}
              onMouseMove={move}
              onMouseUp={end}
              onMouseLeave={end}
              onTouchStart={begin}
              onTouchMove={move}
              onTouchEnd={end}
              data-testid="whiteboard-canvas"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ToolBtn({ active, onClick, label, icon, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border font-mono uppercase tracking-widest text-[11px] transition-colors ${
        active
          ? "border-blue-500/55 bg-blue-500/15 text-blue"
          : "border-white/10 bg-white/[0.03] text-soft hover:bg-white/[0.06]"
      }`}
    >
      {icon}{label}
    </button>
  );
}
